import React, { useEffect, useState } from 'react';
import {
  Typography,
  Button,
  Space,
  Card,
  Row,
  Col,
  Spin,
  App,
  Segmented,
  Tag,
} from 'antd';
import { useNavigate } from 'react-router-dom';
import { apiClient } from '../services/api';
import { useTranslation } from 'react-i18next';
import Seo from '../components/seo/Seo';

const { Title, Paragraph } = Typography;

interface Brand {
  id: number;
  name: string;
  description: string;
  categories: string[];
}

const HomePage: React.FC = () => {
  const navigate = useNavigate();
  const [brands, setBrands] = useState<Brand[]>([]);
  const [topRated, setTopRated] = useState<
    Array<Brand & { overallScore?: number; ratingUpdatedAt?: string }>
  >([]);
  const [trending, setTrending] = useState<
    Array<Brand & { overallScore?: number; ratingUpdatedAt?: string }>
  >([]);
  const [loading, setLoading] = useState(true);
  const { message } = App.useApp();
  const { t } = useTranslation();

  useEffect(() => {
    const fetchBrands = async () => {
      try {
        // 基础品牌用于兜底显示
        const baseResp = await apiClient.getBrands({ limit: 12 });
        if (baseResp.success) {
          setBrands(baseResp.data as Brand[]);
        }

        // 并行获取聚合榜单（一次查询即可）
        const [topResp, trendResp] = await Promise.all([
          apiClient.getTopRatedBrands(3),
          apiClient.getTrendingRatedBrands(3, 30),
        ]);

        if (topResp?.success)
          setTopRated(
            topResp.data as unknown as Array<
              Brand & { overallScore?: number; ratingUpdatedAt?: string }
            >
          );
        if (trendResp?.success)
          setTrending(
            trendResp.data as unknown as Array<
              Brand & { overallScore?: number; ratingUpdatedAt?: string }
            >
          );
      } catch (error) {
        message.error(t('common.error'));
      } finally {
        setLoading(false);
      }
    };

    fetchBrands();
  }, []);

  const [mode, setMode] = useState<'top' | 'trending'>('top');

  const displayBrands = (() => {
    if (mode === 'top') {
      if (topRated.length > 0) return topRated;
    } else {
      if (trending.length > 0) return trending;
    }
    // 兜底：基础品牌前3
    return brands.slice(0, 3);
  })();

  return (
    <div style={{ padding: '24px' }}>
      <Seo
        title={t('brand')}
        description={t('home.subtitle')}
        path={'/'}
        alternates={[
          { hrefLang: 'en', href: `${(import.meta as any).env.VITE_SITE_URL || ''}/?lng=en` },
          { hrefLang: 'zh-CN', href: `${(import.meta as any).env.VITE_SITE_URL || ''}/?lng=zh-CN` },
        ]}
        jsonLd={{
          '@context': 'https://schema.org',
          '@type': 'Organization',
          name: 'LuxEthics',
          url: (import.meta.env.VITE_SITE_URL as string) || 'https://example.com',
          logo: '/logo-light.svg'
        }}
      />
      <div style={{ textAlign: 'center', marginBottom: '50px' }}>
        <Title level={1}>LuxEthics</Title>
        <Paragraph style={{ fontSize: '18px', marginBottom: '30px' }}>
          {t('home.subtitle')}
        </Paragraph>
        <Space size="large">
          <Button
            type="primary"
            size="large"
            onClick={() => navigate('/brands')}
          >
            {t('home.explore')}
          </Button>
          <Button size="large" onClick={() => navigate('/recommendations')}>
            {t('home.recommendations')}
          </Button>
          <Button size="large" onClick={() => navigate('/world-map')}>
            {t('home.map')}
          </Button>
        </Space>
      </div>

      {/* 品牌展示部分 */}
      <div>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 16,
            marginBottom: 24,
          }}
        >
          <Title level={2} style={{ margin: 0 }}>
            {t('home.popular')}
          </Title>
          <Segmented
            size="large"
            value={mode}
            onChange={val => setMode(val as 'top' | 'trending')}
            options={[
              { label: t('home.popular_top'), value: 'top' },
              { label: t('home.popular_trending'), value: 'trending' },
            ]}
          />
        </div>
        {loading ? (
          <div style={{ textAlign: 'center', padding: '50px' }}>
            <Spin size="large" />
          </div>
        ) : (
          <Row gutter={[24, 24]}>
            {displayBrands.map((brand, _idx) => (
              <Col xs={24} sm={12} md={8} key={brand.id}>
                <Card
                  hoverable
                  title={
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                      }}
                    >
                      <span>{brand.name}</span>
                      {mode === 'top' && (
                        <Tag color="gold">{t('home.top_rated_badge')}</Tag>
                      )}
                    </div>
                  }
                  onClick={() => navigate(`/brands/${brand.id}`)}
                  style={{ height: '200px' }}
                >
                  <Paragraph ellipsis={{ rows: 3 }}>
                    {brand.description}
                  </Paragraph>
                  <div style={{ marginTop: '10px' }}>
                    {brand.categories.slice(0, 2).map((category: string) => (
                      <span
                        key={category}
                        style={{
                          display: 'inline-block',
                          background: '#f0f0f0',
                          padding: '2px 8px',
                          borderRadius: '4px',
                          fontSize: '12px',
                          marginRight: '8px',
                        }}
                      >
                        {category}
                      </span>
                    ))}
                  </div>
                </Card>
              </Col>
            ))}
          </Row>
        )}

        {!loading && brands.length > 0 && (
          <div style={{ textAlign: 'center', marginTop: '30px' }}>
            <Button
              type="link"
              size="large"
              onClick={() => navigate('/brands')}
            >
              {t('home.view_more')}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

export default HomePage;
